/**
 * RBAC 데이터 계약 (백엔드·프론트 공용) — docs/spec/rbac-permission-model.md
 *
 * 코드 role 4종을 기준으로 권한 매트릭스를 저장한다. 사용자에게 보이는 "표시 레벨"은
 * deriveDisplayLevel()로 role+소속 여부에서 파생한다(기존 매장 admin은 "매장대표"로 보인다).
 */

/** DB profiles.role / role_default_permissions.role 의 코드값 */
// lead = 데모신청 리드 본인(sales_leads 에 연결된 계정). 최소권한 — 관리자/매장 운영
// 화면엔 일절 못 들어가고, 본체(phoneboard.kr)에 로그인하면 자기 전용 매장 정책으로
// 시연 화면만 본다(범위의 SSOT 는 src/lib/leadDemo.ts).
//
// ⚠️ **2026-08-28: `superadmin` 을 없앴다.** 개발자와 본사 서비스 관리자를 나눠 뒀지만
//    화면에선 이미 «관리자» 하나로 보이고 있었고, 운영상 두 계정이 각 1명씩이라 구분에
//    실익이 없었다. 이제 본사 관리자는 `admin` 하나다(마이그 0310 이 기존 행을 옮긴다).
//    옛 값은 아래 두 판정 함수가 **별칭으로 계속 받는다** — 값이 어디 한 군데 남아 있어도
//    그 계정이 매장직원으로 굴러떨어지지 않게 하는 안전장치다. 지우지 말 것.
export const ROLE_CODES = ["admin", "owner", "tenant", "lead"] as const;
export type RoleCode = (typeof ROLE_CODES)[number];

/** 없어진 옛 코드값 — 아직 DB·토큰에 남아 있을 수 있어 판정 함수가 별칭으로 받는다. */
const LEGACY_SUPERADMIN = "superadmin";

/** 권한 단위 = LNB 메뉴 그룹 (+ 메뉴가 아닌 "본사 위임 기능" 묶음 hq_shared) */
// hq_shared 는 다른 키들과 성격이 다르다 — 사이드바 메뉴가 아니라 **본사가 소유한 전 매장
// 공용 데이터를 고칠 권한**이다(공통 모델 그룹 등). 매장 데이터 범위는 건드리지 않고 이
// 기능만 매장 계정에 위임할 수 있게, 개인 예외(user_permission_overrides)로 켜는 칸을 둔다.
// (예전엔 "라인업 매장 대표만 예외" 를 코드에 매장 id 로 박아 넣었다 — auth-middleware 의
//  requireInboundMessageAccess·requireStoreLeadAccess 가 그 잔재다. 새 위임은 여기로 모은다.)
// ⚠️ 메뉴가 없으므로 LANDING_ORDER(src/lib/landing.ts)·AppSidebar 매핑에는 넣지 않는다.
export const PAGE_GROUP_KEYS = [
  "crm",
  "policy",
  "inventory",
  "kakao_counsel",
  "admin",
  "hq_shared",
] as const;
export type PageGroupKey = (typeof PAGE_GROUP_KEYS)[number];

/** 권한 수준 */
export const PERM_LEVELS = ["none", "read", "write"] as const;
export type PermLevelCode = (typeof PERM_LEVELS)[number];

export type PermMatrix = Record<RoleCode, Record<PageGroupKey, PermLevelCode>>;

/**
 * 개인별 권한 예외(오버라이드) 맵 — 특정 사람 한 명에게 역할 기본값과 다른 권한을 줄 때.
 * 지정된 그룹만 역할 기본값을 덮고, 없는 그룹은 역할 기본값을 그대로 따른다(부분 맵).
 * 저장 SSOT: DB user_permission_overrides (userId × tenantId × pageGroup → permLevel).
 */
export type PermOverrideMap = Partial<Record<PageGroupKey, PermLevelCode>>;

/** 역할별 기본 권한 매트릭스 초기값 (spec §5). DB가 비어 있을 때 시드·폴백으로 쓴다. */
// tickets(슬랙 대화 자동 티켓화)는 본사 내부 운영 도구다 — 슬랙 내부 대화가 원본이라
// 매장대표(owner)·매장직원(tenant)에게는 노출하지 않는다(none). 본사 관리자만 read/write.
// hq_shared(본사 공용 데이터) 기본값 — 본사 관리자만 write, 매장은 read(보되 못 고침),
// 리드는 none. 매장에 위임할 땐 이 기본값을 개인 예외로 덮는다(코드 수정 없이 화면에서).
export const DEFAULT_MATRIX: PermMatrix = {
  admin: { crm: "write", policy: "write", inventory: "write", kakao_counsel: "write", admin: "write", hq_shared: "write" },
  owner: { crm: "write", policy: "write", inventory: "write", kakao_counsel: "write", admin: "read", hq_shared: "read" },
  // 카톡 상담은 고객 응대라 매장직원도 답변 전송이 필요 → crm 과 같은 write 기본값.
  tenant: { crm: "write", policy: "read", inventory: "read", kakao_counsel: "write", admin: "none", hq_shared: "read" },
  // 리드 본인(승격 전) — 영업 시연을 위해 **정책만 읽기**로 연다. 리드는 매장 무소속이라
  // 서버가 시연용 매장으로 스코프하므로(getTenantId), 우리 고객 매장 데이터엔 닿지 않는다.
  // 읽기라 업로드·수정은 전부 403. 화면은 여기서 한 번 더 좁힌다 — 정책 그룹엔 관제·별칭 같은
  // 내부 운영 화면이 섞여 있어, 예비 고객에게 보일 화면만 골라 둔 명단이 따로 있다
  // (src/lib/leadDemo.ts). 승격되면 이 역할을 벗어나 owner 가 되므로 이 줄과 무관해진다.
  lead: { crm: "none", policy: "read", inventory: "none", kakao_counsel: "none", admin: "none", hq_shared: "none" },
};

/** 표시 레벨 라벨 (코드 role 기준 행 라벨).
 *  본사 관리자 vs 매장대표는 권한 범위가 본질적으로 달라(전 매장 vs 자기 매장) 라벨을 나눠 둔다. */
export const ROLE_LABEL: Record<RoleCode, string> = {
  admin: "관리자",
  owner: "매장대표",
  tenant: "매장직원",
  lead: "신청자",
};

/**
 * 개인의 "표시 레벨"을 코드 role + 매장 소속 여부에서 파생한다.
 * - 기존 매장 admin(매장 소속)은 사실상 매장대표 → "owner"로 표시(코드는 admin 유지).
 * - 매장 무소속 admin은 서비스 관리자 → "admin".
 */
export function deriveDisplayLevel(
  role: string,
  hasTenant: boolean,
): RoleCode {
  // 옛 superadmin 값이 남아 있어도 본사 관리자로 읽는다 — 이 별칭이 없으면 아래 폴백으로
  // 떨어져 **개발자 계정이 매장직원 권한으로 강등**된다(스스로 되돌릴 수도 없다).
  if (role === LEGACY_SUPERADMIN) return "admin";
  if (role === "admin") return hasTenant ? "owner" : "admin";
  if (role === "owner") return "owner";
  // ⚠️ 리드는 반드시 이 폴백 앞에서 분기한다 — 빼면 아래 "return tenant" 로 떨어져
  //    리드가 매장직원 권한(crm=write 등)을 얻는 권한상승 버그가 난다.
  if (role === "lead") return "lead";
  return "tenant";
}

/**
 * "전역(본사) 관리자" 여부를 코드 role + 매장 소속에서 판정한다 — 단일 SSOT.
 *
 * 전역 = 매장에 묶이지 않은 admin(본사 관리자). 매장에 묶인 admin은
 * 화면상 "매장대표"라 전역이 아니다(deriveDisplayLevel(admin, hasTenant)=owner 와 같은 사상).
 *
 * ⚠️ 이 판정을 직접 인라인으로 (role==="admin") 만 보고 복붙하면 안 된다 —
 *    매장 소속 admin 이 전역으로 오인돼 임의 매장 전환·전 매장 열람 같은 수평 권한 상승이 생긴다.
 *    requireGlobalAdmin·회원관리 getCallerScope·매장 전환(switch)·매장 목록·프론트 isGlobalAdmin 이
 *    모두 이 한 함수를 거쳐 기준이 갈리지 않게 한다.
 */
export function isGlobalAdminScope(
  role: string | null | undefined,
  tenantId: string | null | undefined,
): boolean {
  // 옛 superadmin 은 언제나 매장 무소속이었으므로 전역으로 받는다(별칭 — 위 주석 참조).
  return role === LEGACY_SUPERADMIN || (role === "admin" && tenantId == null);
}

/** 멤버십 role(매장 안에서의 역할)로 허용되는 코드값 */
export const MEMBERSHIP_ROLES = ["owner", "tenant"] as const;

// ───────────────────────────────────────────────────────────────────────────
// 권한 강제(enforcement) 공용 헬퍼 — 백엔드 미들웨어·프론트 가드가 모두 이걸 쓴다.
//
// ⚠️ 핵심 규칙: 권한 매트릭스를 조회하는 행 키는 DB의 role 코드값이 아니라
//    deriveDisplayLevel(role, 매장소속여부)의 결과(표시 레벨)다.
//    그래야 "기존 매장 admin(코드는 admin, 표시는 매장대표)"이 admin 행(전권)이 아니라
//    owner 행 권한을 받아 표시 레벨과 권한이 일치한다. (rbac §2)
// ───────────────────────────────────────────────────────────────────────────

/**
 * 한 사용자가 특정 페이지 그룹에서 갖는 최종 권한 수준을 계산한다.
 * @param matrix      현재 권한 매트릭스(DB 저장값 + DEFAULT_MATRIX 폴백). 없으면 기본값 사용.
 * @param role        DB profiles.role 코드값
 * @param hasTenant   매장 소속 여부(profiles.tenantId 또는 멤버십 존재)
 * @param group       대상 페이지 그룹
 * @param override    개인별 예외 맵. 해당 그룹에 값이 있으면 역할 기본값보다 우선한다.
 *
 * 우선순위: 개인 예외(override) > 역할 기본 매트릭스(matrix) > DEFAULT_MATRIX.
 * override 인자는 옵셔널이라 기존 호출부(2~4인자)는 그대로 동작한다(예외 없는 = 종전 동작).
 */
export function resolvePermLevel(
  role: string,
  hasTenant: boolean,
  group: PageGroupKey,
  matrix: PermMatrix = DEFAULT_MATRIX,
  override?: PermOverrideMap,
): PermLevelCode {
  const o = override?.[group];
  if (o) return o;
  const effectiveRole = deriveDisplayLevel(role, hasTenant);
  return matrix[effectiveRole]?.[group] ?? DEFAULT_MATRIX[effectiveRole][group];
}

/**
 * 사용자의 전체 그룹별 권한을 한 번에 계산한다(auth/me 응답용).
 * 프론트는 이 맵만 보고 메뉴/버튼/라우트를 제어한다.
 * @param override 개인별 예외 맵(있으면 그룹별로 역할 기본값을 덮는다).
 */
export function resolveUserPermissions(
  role: string,
  hasTenant: boolean,
  matrix: PermMatrix = DEFAULT_MATRIX,
  override?: PermOverrideMap,
): Record<PageGroupKey, PermLevelCode> {
  const out = {} as Record<PageGroupKey, PermLevelCode>;
  for (const g of PAGE_GROUP_KEYS) out[g] = resolvePermLevel(role, hasTenant, g, matrix, override);
  return out;
}

/** 권한 수준이 "접근 가능"(읽기 이상)인가 — 메뉴 노출·라우트 진입 기준 */
export function canAccess(level: PermLevelCode): boolean {
  return level === "read" || level === "write";
}

/** 권한 수준이 "편집 가능"(쓰기)인가 — 생성·수정·삭제 버튼/API 기준 */
export function canWrite(level: PermLevelCode): boolean {
  return level === "write";
}

/**
 * 상담 챗봇 **훈련장**(훈련 대화·말투 설정·데이터소스 매핑·학습 현황판)에 들어갈 수 있는가.
 *
 * ## 왜 kakao_counsel 권한만으로는 부족한가
 *
 * kakao_counsel 은 **고객 응대** 권한이라 매장직원까지 write 다(§8) — 손님 답변은 누구나 보내야
 * 하니까. 그런데 훈련장은 응대가 아니라 **챗봇의 성격 자체를 바꾸는 자리**다. 여기서 저장한
 * 말투 한 줄·규칙 한 장이 그 매장의 모든 답변에 적용되고, 잘못 배운 규칙은 조용히 오래 남는다.
 * 그래서 "답변은 직원도, 챗봇을 고치는 건 대표·관리자만"으로 한 칸 더 좁힌다(2026-08-18 결정).
 *
 * 판정은 둘을 **모두** 만족해야 한다:
 *  1. kakao_counsel 권한이 write (읽기만으로는 안 된다 — 훈련장은 본질이 저장이다)
 *  2. 표시 레벨이 admin · owner 중 하나 (매장직원 tenant·리드 lead 제외)
 *
 * ⚠️ 서버 라우트와 프론트 화면 **양쪽에서** 이 한 함수를 쓴다. 프론트에서만 막으면 API 는 열려
 *    있고, 서버에서만 막으면 직원 화면에 눌러도 403 나는 메뉴가 뜬다.
 * ⚠️ role 을 직접 비교해 복붙하지 말 것 — 매장 소속 admin 은 코드상 admin 이지만 표시는 owner 라
 *    반드시 deriveDisplayLevel 을 거쳐야 기준이 갈리지 않는다(isGlobalAdminScope 주석과 같은 사상).
 *
 * @param role      DB profiles.role 코드값
 * @param hasTenant 매장 소속 여부
 * @param perms     이 사용자의 그룹별 최종 권한 맵(resolveUserPermissions 결과).
 *                  없으면 역할 기본 매트릭스로 계산한다.
 */
export function canAccessCounselTraining(
  role: string,
  hasTenant: boolean,
  perms?: Partial<Record<PageGroupKey, PermLevelCode>>,
): boolean {
  const level = perms?.kakao_counsel ?? resolvePermLevel(role, hasTenant, "kakao_counsel");
  if (!canWrite(level)) return false;
  const displayLevel = deriveDisplayLevel(role, hasTenant);
  return displayLevel === "admin" || displayLevel === "owner";
}

/**
 * HTTP 메서드가 "쓰기"(변경) 요청인지 판정한다.
 * GET·HEAD·OPTIONS는 읽기, 그 외(POST·PUT·PATCH·DELETE)는 쓰기.
 * 백엔드 미들웨어가 read 권한 사용자의 변경 요청을 거를 때 쓴다.
 */
export function methodIsWrite(method: string): boolean {
  const m = method.toUpperCase();
  return m !== "GET" && m !== "HEAD" && m !== "OPTIONS";
}
